// The whole game loop, rendered against a fixed 1152×768 "virtual" stage that
// is scaled to fit any window — so all layout math uses constants and resizing
// is just one transform on the world container.
import {
  Application,
  BlurFilter,
  Container,
  FederatedPointerEvent,
  FillGradient,
  Graphics,
  Text,
  type Ticker,
} from "pixi.js";
import { palette, FONT } from "./theme";
import { Card } from "./card";
import { pressMeterAt } from "./press";
import { makeProblem, type Problem, type ProblemOptions, type Op } from "./problem";
import { levelFor, problemOptsFor } from "./progression";
import { scoreFor } from "./score";
import { speedMeterAt } from "./speed";
import { loadStats, pickWeakest, recordAttempt, recordSession, saveStats } from "./stats";
import { OP_ORDER, OP_GLYPH, loadOps, saveOps } from "./ops";
import { typedMatch } from "./keyboard";
import { Chip, StartButton } from "./ui";
import { Ease, burst, cancelMotion, delay, fadeTo, moveTo, rotateTo, scaleTo, shake, tween } from "./juice";
import { Sfx, isMuted, setMuted, unlockAudio } from "./sound";
import {
  initViewport,
  isFullscreen,
  isFullscreenSupported,
  onFullscreenChange,
  requestImmersive,
  toggleFullscreen,
} from "./viewport";

const VW = 1152;
const VH = 768;

function clamp(v: number, lo: number, hi: number) {
  return Math.max(lo, Math.min(hi, v));
}

function lerpColor(a: number, b: number, t: number): number {
  const ar = (a >> 16) & 255;
  const ag = (a >> 8) & 255;
  const ab = a & 255;
  const br = (b >> 16) & 255;
  const bg = (b >> 8) & 255;
  const bb = b & 255;
  const r = Math.round(ar + (br - ar) * t);
  const g = Math.round(ag + (bg - ag) * t);
  const bl = Math.round(ab + (bb - ab) * t);
  return (r << 16) | (g << 8) | bl;
}

interface Blob {
  g: Graphics;
  bx: number;
  by: number;
  sp: number;
  ph: number;
}

export class Game {
  private readonly app: Application;

  // layer stack
  private world = new Container();
  private bgLayer = new Container();
  private shakeRoot = new Container();
  private boardLayer = new Container();
  private handLayer = new Container();
  private fxLayer = new Container();
  private hudLayer = new Container();
  private titleLayer = new Container();

  // tunables
  private readonly cardW = 96;
  private readonly cardH = 134;
  private readonly slotW = 106;
  private readonly slotH = 146;
  private readonly speedTrackW = 480;
  private readonly speedWindow = 3600; // ms for the speed bonus to fully drain
  private readonly speedHoldMs = 500; // full-bonus hold (bar pulses) before draining
  private readonly baseChips = 10;
  private readonly doubleTapMs = 350; // a second click/tap on a card within this commits it
  private readonly holdMs = 480; // press & hold a card this long (in place) to commit it
  private readonly holdMoveCancelPx = 12; // moving past this (virtual px) turns the hold into a drag

  // persistent display objects
  private slot = new Container();
  private slotGlow = new Graphics();
  private speedBar = new Container();
  private speedFill = new Graphics();
  private spotlight = new Graphics();
  private blobs: Blob[] = [];
  private scoreValue!: Text;
  private streakText!: Text;
  private levelText!: Text;
  private hint!: Text;
  private soundBtn!: Text;
  private fsBtn?: Text;
  private startButton!: StartButton;
  private opChips: Chip[] = [];

  // per-round display objects
  private eqTokens: Text[] = [];
  private cards: Card[] = [];

  // state
  private state: "title" | "playing" = "title";
  private selectedOps: Set<Op> = loadOps();
  private stats = loadStats();
  private problem!: Problem;
  private score = 0;
  private displayScore = 0;
  private streak = 0;
  private bestStreak = 0;
  private totalCorrect = 0;
  private resolving = false;
  private dragging: Card | null = null;
  private lastTapCard: Card | null = null;
  private lastTapTime = 0;
  private pressCard: Card | null = null;
  private pressStartClock = 0;
  private pressDownX = 0;
  private pressDownY = 0;
  private pressRing = new Graphics();
  private grabDX = 0;
  private grabDY = 0;
  private lastPX = 0;
  private slotHovered = false;
  private hintHidden = false;
  private clock = 0;
  private roundStartClock = 0;
  private speedMeter = 1;
  private slotQ!: Text;
  private typedBuffer = "";
  private typedDeadline = 0; // clock time when a pending exact match auto-commits

  constructor(app: Application) {
    this.app = app;

    app.stage.addChild(this.world);
    this.world.addChild(this.bgLayer, this.shakeRoot, this.hudLayer, this.titleLayer);
    this.shakeRoot.addChild(this.boardLayer, this.handLayer, this.fxLayer);
    this.fxLayer.addChild(this.pressRing); // long-press charge ring, drawn each tick

    this.buildBackground();
    this.buildSlot();
    this.buildSpeedBar();
    this.buildHud();
    this.buildOpBar();
    this.buildTitle();
    this.refreshChips();

    // start on the title screen — the board & HUD fade in when the player hits Start
    this.shakeRoot.visible = false;
    this.hudLayer.visible = false;

    app.stage.eventMode = "static";
    app.stage.hitArea = app.screen;
    app.stage.on("pointermove", this.onMove);
    app.stage.on("pointerup", this.onUp);
    app.stage.on("pointerupoutside", this.onUp);
    window.addEventListener("keydown", this.onKey);

    app.ticker.add(this.tick);

    this.resize();
    // viewport.ts owns the resize/orientation/fullscreen listeners and the
    // rotate-gate; it calls this.resize() to re-fit the canvas on any change.
    initViewport(this.resize);
  }

  start() {
    this.showTitle();
  }

  // ── title screen ↔ play ───────────────────────────────────────────────────
  private showTitle() {
    this.state = "title";
    this.shakeRoot.visible = false;
    this.hudLayer.visible = false;
    this.titleLayer.visible = true;
    this.titleLayer.alpha = 1;
  }

  private beginPlay() {
    if (this.state === "playing") return;
    this.state = "playing";
    unlockAudio();
    requestImmersive(); // chrome-free + landscape lock (best-effort) on the Start tap
    Sfx.start();
    this.shakeRoot.visible = true;
    this.hudLayer.visible = true;
    this.shakeRoot.alpha = 1;
    this.hudLayer.alpha = 1;
    burst(this.fxLayer, this.startButton.x, this.startButton.y, [palette.gold, 0xffffff, palette.glow, palette.chip], 44);
    fadeTo(this.titleLayer, 0, 320, Ease.outCubic, () => {
      this.titleLayer.visible = false;
      this.titleLayer.alpha = 1;
    });
    this.newRound();
  }

  // ── operator selection ────────────────────────────────────────────────────
  private onChipTap(op: Op | "all") {
    unlockAudio();
    if (op === "all") {
      for (const o of OP_ORDER) this.selectedOps.add(o);
      Sfx.toggle(true);
    } else if (this.selectedOps.has(op)) {
      if (this.selectedOps.size <= 1) {
        // keep at least one operator in play — nudge the chip instead of removing it
        for (const c of this.opChips) {
          if (c.op === op) scaleTo(c, 1.18, 90, Ease.outBack, () => scaleTo(c, 1, 160, Ease.outCubic));
        }
        Sfx.hover();
        return;
      }
      this.selectedOps.delete(op);
      Sfx.toggle(false);
    } else {
      this.selectedOps.add(op);
      Sfx.toggle(true);
    }
    saveOps(this.selectedOps);
    this.refreshChips();
    this.startButton.setEnabled(this.selectedOps.size > 0);
  }

  private refreshChips() {
    const allOn = OP_ORDER.every((o) => this.selectedOps.has(o));
    for (const c of this.opChips) {
      c.setSelected(c.op === "all" ? allOn : this.selectedOps.has(c.op));
    }
  }

  // ── difficulty (the ramp itself lives in progression.ts, pure & tested) ──
  private get level() {
    return levelFor(this.totalCorrect);
  }

  private problemOpts(): ProblemOptions {
    return {
      ...problemOptsFor(this.level),
      ops: this.selectedOps.size ? [...this.selectedOps] : ["+"],
    };
  }

  // ── scene construction ──────────────────────────────────────────────────
  private buildBackground() {
    const bg = new Graphics();
    try {
      const grad = new FillGradient({
        type: "linear",
        start: { x: 0, y: -200 },
        end: { x: 0, y: VH + 200 },
        colorStops: [
          { offset: 0, color: palette.bgTop },
          { offset: 1, color: palette.bgBottom },
        ],
        textureSpace: "local",
      });
      bg.rect(-400, -400, VW + 800, VH + 800).fill(grad);
    } catch {
      bg.rect(-400, -400, VW + 800, VH + 800).fill(palette.bgBottom);
    }
    this.bgLayer.addChild(bg);

    const soft = new Container();
    this.bgLayer.addChild(soft);

    this.spotlight.circle(0, 0, 470).fill({ color: 0x357f81, alpha: 0.5 });
    this.spotlight.position.set(VW / 2, VH * 0.4);
    soft.addChild(this.spotlight);

    const blobColors = [palette.blob1, palette.blob2, palette.glow, palette.mult];
    for (let i = 0; i < 4; i++) {
      const g = new Graphics();
      const r = 130 + Math.random() * 150;
      g.circle(0, 0, r).fill({ color: blobColors[i % blobColors.length]!, alpha: 0.12 });
      const bx = Math.random() * VW;
      const by = Math.random() * VH;
      g.position.set(bx, by);
      soft.addChild(g);
      this.blobs.push({ g, bx, by, sp: 0.15 + Math.random() * 0.35, ph: Math.random() * Math.PI * 2 });
    }

    try {
      soft.filters = [new BlurFilter({ strength: 40, quality: 3 })];
    } catch {
      /* unblurred discs are an acceptable fallback */
    }
  }

  private buildSlot() {
    const w = this.slotW;
    const h = this.slotH;
    this.slotGlow.roundRect(-w / 2 - 8, -h / 2 - 8, w + 16, h + 16, 22).fill(palette.slotEdge);
    this.slotGlow.alpha = 0.25;
    this.slot.addChild(this.slotGlow);

    const bg = new Graphics();
    bg.roundRect(-w / 2, -h / 2, w, h, 16)
      .fill({ color: palette.slot, alpha: 0.55 })
      .stroke({ width: 3, color: palette.slotEdge });
    this.slot.addChild(bg);

    // shows "?" — or the digits the player is typing (keyboard answers)
    this.slotQ = new Text({
      text: "?",
      style: { fontFamily: FONT, fontWeight: "700", fontSize: h * 0.5, fill: palette.slotEdge },
    });
    this.slotQ.anchor.set(0.5);
    this.slotQ.alpha = 0.45;
    this.slot.addChild(this.slotQ);

    this.boardLayer.addChild(this.slot);
  }

  private buildSpeedBar() {
    const track = new Graphics();
    track
      .roundRect(-this.speedTrackW / 2, -8, this.speedTrackW, 16, 8)
      .fill({ color: 0x000000, alpha: 0.3 })
      .stroke({ width: 2, color: palette.slotEdge, alpha: 0.5 });
    this.speedBar.addChild(track);
    this.speedBar.addChild(this.speedFill);

    const lbl = new Text({
      text: "SPEED BONUS",
      style: { fontFamily: FONT, fontWeight: "600", fontSize: 13, fill: palette.textDim, letterSpacing: 3 },
    });
    // label rides just above the track — the bar sits above the equation
    lbl.anchor.set(0.5, 1);
    lbl.position.set(0, -12);
    this.speedBar.addChild(lbl);

    this.boardLayer.addChild(this.speedBar);
  }

  private buildHud() {
    const title = new Text({
      text: "GETMATHSKILLS",
      style: { fontFamily: FONT, fontWeight: "700", fontSize: 26, fill: palette.glow, letterSpacing: 2 },
    });
    title.position.set(30, 22);
    title.alpha = 0.92;
    this.hudLayer.addChild(title);

    const sub = new Text({
      text: "card math",
      style: { fontFamily: FONT, fontWeight: "500", fontSize: 15, fill: palette.textDim, letterSpacing: 5 },
    });
    sub.position.set(32, 54);
    this.hudLayer.addChild(sub);

    const scoreCap = new Text({
      text: "SCORE",
      style: { fontFamily: FONT, fontWeight: "600", fontSize: 17, fill: palette.textDim, letterSpacing: 6 },
    });
    scoreCap.anchor.set(0.5, 0);
    scoreCap.position.set(VW / 2, 20);
    this.hudLayer.addChild(scoreCap);

    this.scoreValue = new Text({
      text: "0",
      style: {
        fontFamily: FONT,
        fontWeight: "700",
        fontSize: 62,
        fill: palette.gold,
        dropShadow: { color: 0x000000, alpha: 0.4, blur: 6, distance: 4, angle: Math.PI / 2 },
      },
    });
    this.scoreValue.anchor.set(0.5, 0.5);
    this.scoreValue.position.set(VW / 2, 76);
    this.hudLayer.addChild(this.scoreValue);

    this.streakText = new Text({
      text: "",
      style: { fontFamily: FONT, fontWeight: "700", fontSize: 30, fill: palette.gold },
    });
    this.streakText.anchor.set(1, 0.5);
    this.streakText.position.set(VW - 30, 42);
    this.hudLayer.addChild(this.streakText);

    this.levelText = new Text({
      text: "LV 1",
      style: { fontFamily: FONT, fontWeight: "600", fontSize: 18, fill: palette.textDim, letterSpacing: 2 },
    });
    this.levelText.anchor.set(1, 0.5);
    this.levelText.position.set(VW - 30, 74);
    this.hudLayer.addChild(this.levelText);

    this.hint = new Text({
      text: "drag, double-tap, press & hold — or just type the missing number. fast!",
      style: { fontFamily: FONT, fontWeight: "500", fontSize: 22, fill: palette.textDim },
    });
    this.hint.anchor.set(0.5);
    this.hint.position.set(VW / 2, VH * 0.86);
    this.hudLayer.addChild(this.hint);

    this.soundBtn = new Text({
      text: "SOUND: ON",
      style: { fontFamily: FONT, fontWeight: "600", fontSize: 16, fill: palette.textDim, letterSpacing: 1 },
    });
    this.soundBtn.anchor.set(0, 1);
    this.soundBtn.position.set(30, VH - 24);
    this.soundBtn.eventMode = "static";
    this.soundBtn.cursor = "pointer";
    this.soundBtn.on("pointertap", () => {
      const m = !isMuted();
      setMuted(m);
      unlockAudio();
      this.soundBtn.text = m ? "SOUND: OFF" : "SOUND: ON";
      this.soundBtn.alpha = m ? 0.5 : 1;
    });
    this.hudLayer.addChild(this.soundBtn);

    // Fullscreen toggle, mirroring the SOUND control but bottom-RIGHT so it
    // clears the bottom-left SOUND text and the bottom-center PRACTICE bar.
    // Hidden entirely where the Fullscreen API is unavailable (e.g. iPhone),
    // so there is never a dead control.
    if (isFullscreenSupported()) {
      this.fsBtn = new Text({
        text: "FULLSCREEN",
        style: { fontFamily: FONT, fontWeight: "600", fontSize: 16, fill: palette.textDim, letterSpacing: 1 },
      });
      this.fsBtn.anchor.set(1, 1);
      this.fsBtn.position.set(VW - 30, VH - 24);
      this.fsBtn.eventMode = "static";
      this.fsBtn.cursor = "pointer";
      this.fsBtn.on("pointertap", () => {
        unlockAudio();
        toggleFullscreen();
      });
      this.hudLayer.addChild(this.fsBtn);
      onFullscreenChange(() => this.updateFsButton());
    }
  }

  private updateFsButton() {
    if (this.fsBtn) this.fsBtn.text = isFullscreen() ? "WINDOWED" : "FULLSCREEN";
  }

  private chipSpecs(allSize: number, opSize: number) {
    return [
      { text: "All", op: "all" as const, size: allSize },
      ...OP_ORDER.map((op) => ({ text: OP_GLYPH[op], op, size: opSize })),
    ];
  }

  private buildTitle() {
    const cx = VW / 2;

    const title = new Text({
      text: "GETMATHSKILLS",
      style: {
        fontFamily: FONT,
        fontWeight: "700",
        fontSize: 76,
        fill: palette.gold,
        letterSpacing: 2,
        dropShadow: { color: 0x000000, alpha: 0.45, blur: 8, distance: 5, angle: Math.PI / 2 },
      },
    });
    title.anchor.set(0.5);
    title.position.set(cx, 136);
    this.titleLayer.addChild(title);

    const tag = new Text({
      text: "fast facts • big fun",
      style: { fontFamily: FONT, fontWeight: "500", fontSize: 26, fill: palette.glow, letterSpacing: 4 },
    });
    tag.anchor.set(0.5);
    tag.position.set(cx, 190);
    this.titleLayer.addChild(tag);

    const goal = new Text({
      text: "Find the missing number — fast!",
      style: { fontFamily: FONT, fontWeight: "600", fontSize: 22, fill: palette.text },
    });
    goal.anchor.set(0.5);
    goal.position.set(cx, 238);
    this.titleLayer.addChild(goal);

    // How you answer. A quiet label, then one icon-over-one-word column per
    // method — four short reads instead of one long line. Deliberately NOT
    // chip-shaped: the operator chips below ARE tappable, and look-alike tiles
    // up here would read as dead buttons to a kid.
    const how = new Text({
      text: "CHOOSE THE RIGHT NUMBER BY",
      style: { fontFamily: FONT, fontWeight: "600", fontSize: 17, fill: palette.textDim, letterSpacing: 4 },
    });
    how.anchor.set(0.5);
    how.position.set(cx, 282);
    this.titleLayer.addChild(how);

    const methods = [
      { icon: "✋", word: "DRAG" },
      { icon: "👆👆", word: "DOUBLE-TAP" },
      { icon: "⏳", word: "HOLD" },
      { icon: "⌨️", word: "TYPE" },
    ];
    const pitch = 190; // column spacing — sits a touch wider than the 552px chip row below
    methods.forEach((m, i) => {
      const mx = cx + (i - (methods.length - 1) / 2) * pitch;

      const icon = new Text({ text: m.icon, style: { fontFamily: FONT, fontSize: 30 } });
      icon.anchor.set(0.5);
      icon.position.set(mx, 322);
      this.titleLayer.addChild(icon);

      const word = new Text({
        text: m.word,
        style: { fontFamily: FONT, fontWeight: "600", fontSize: 16, fill: palette.glow, letterSpacing: 1.5 },
      });
      word.anchor.set(0.5);
      word.position.set(mx, 354);
      this.titleLayer.addChild(word);
    });

    const ask = new Text({
      text: "PICK WHAT TO PRACTICE",
      style: { fontFamily: FONT, fontWeight: "600", fontSize: 22, fill: palette.textDim, letterSpacing: 4 },
    });
    ask.anchor.set(0.5);
    ask.position.set(cx, 402);
    this.titleLayer.addChild(ask);

    const specs = this.chipSpecs(30, 46);
    const cw = 96;
    const ch = 78;
    const gap = 18;
    const total = specs.length * cw + (specs.length - 1) * gap;
    let x = cx - total / 2 + cw / 2;
    for (const s of specs) {
      const chip = new Chip(s.text, s.op, cw, ch, s.size);
      chip.position.set(x, 472);
      chip.onTap = () => this.onChipTap(s.op);
      this.titleLayer.addChild(chip);
      this.opChips.push(chip);
      x += cw + gap;
    }

    const note = new Text({
      text: "change these anytime while you play",
      style: { fontFamily: FONT, fontWeight: "500", fontSize: 15, fill: palette.textDim },
    });
    note.anchor.set(0.5);
    note.position.set(cx, 536);
    note.alpha = 0.8;
    this.titleLayer.addChild(note);

    this.startButton = new StartButton(320, 96);
    this.startButton.position.set(cx, 636);
    this.startButton.onActivate = () => this.beginPlay();
    this.titleLayer.addChild(this.startButton);

    // all-time bests (persisted in stats.ts) — quiet flex under the Start button
    if (this.stats.bestScore > 0) {
      const best = new Text({
        text: `BEST ${this.stats.bestScore}   ·   STREAK ×${this.stats.bestStreak}`,
        style: { fontFamily: FONT, fontWeight: "600", fontSize: 20, fill: palette.gold, letterSpacing: 2 },
      });
      best.anchor.set(0.5);
      best.position.set(cx, 722);
      this.titleLayer.addChild(best);
    }
  }

  private buildOpBar() {
    const cx = VW / 2;

    const lbl = new Text({
      text: "PRACTICE",
      style: { fontFamily: FONT, fontWeight: "600", fontSize: 12, fill: palette.textDim, letterSpacing: 3 },
    });
    lbl.anchor.set(0.5);
    lbl.position.set(cx, VH - 64);
    this.hudLayer.addChild(lbl);

    const specs = this.chipSpecs(16, 26);
    const cw = 54;
    const ch = 40;
    const gap = 10;
    const total = specs.length * cw + (specs.length - 1) * gap;
    let x = cx - total / 2 + cw / 2;
    for (const s of specs) {
      const chip = new Chip(s.text, s.op, cw, ch, s.size);
      chip.position.set(x, VH - 34);
      chip.onTap = () => this.onChipTap(s.op);
      this.hudLayer.addChild(chip);
      this.opChips.push(chip);
      x += cw + gap;
    }
  }

  // ── rounds ──────────────────────────────────────────────────────────────
  private newRound() {
    // Deal the fact most in need of practice: sample a few candidates and let
    // the per-fact stats pick the weakest. `avoid` keeps the same fact from
    // repeating back-to-back.
    const opts = { ...this.problemOpts(), avoid: this.problem };
    const candidates = [makeProblem(opts), makeProblem(opts), makeProblem(opts)];
    this.problem = pickWeakest(candidates, this.stats);
    this.buildEquation();
    this.buildCards();
    this.resolving = false;
    this.roundStartClock = this.clock;
    this.speedMeter = 1;
    this.lastTapCard = null;
    this.cancelPress();
    this.clearTyped();
  }

  private nextRound() {
    const tokens = this.eqTokens;
    const cards = this.cards;
    this.eqTokens = [];
    this.cards = [];
    for (const t of tokens) fadeTo(t, 0, 200, Ease.outCubic, () => !t.destroyed && t.destroy());
    for (const c of cards) {
      if (!c.destroyed) fadeTo(c, 0, 200, Ease.outCubic, () => !c.destroyed && c.destroy());
    }
    delay(200, () => this.newRound());
  }

  private buildEquation() {
    const eqFont = 100;
    const gap = 26;

    const items = this.problem.tokens.map((t) => {
      if (t.isBlank) return { text: "", isBlank: true, fill: 0, size: eqFont };
      const isOp = (OP_ORDER as readonly string[]).includes(t.text);
      const isEq = t.text === "=";
      return {
        text: isOp ? OP_GLYPH[t.text as Op] : t.text,
        isBlank: false,
        fill: isOp ? palette.gold : isEq ? palette.textDim : palette.text,
        size: isOp || isEq ? eqFont * 0.64 : eqFont,
      };
    });

    const widths = items.map((it) => {
      if (it.isBlank) return this.slotW;
      const probe = new Text({ text: it.text, style: { fontFamily: FONT, fontWeight: "700", fontSize: it.size } });
      const w = probe.width;
      probe.destroy();
      return w;
    });

    const total = widths.reduce((a, b) => a + b, 0) + gap * (items.length - 1);
    const y = VH * 0.36;
    let x = VW / 2 - total / 2;

    items.forEach((it, i) => {
      const w = widths[i]!;
      const cx = x + w / 2;
      if (it.isBlank) {
        this.slot.position.set(cx, y);
      } else {
        const t = new Text({
          text: it.text,
          style: {
            fontFamily: FONT,
            fontWeight: "700",
            fontSize: it.size,
            fill: it.fill,
            dropShadow: { color: 0x000000, alpha: 0.4, blur: 6, distance: 5, angle: Math.PI / 2 },
          },
        });
        t.anchor.set(0.5);
        t.position.set(cx, y);
        t.scale.set(0.6);
        t.alpha = 0;
        this.boardLayer.addChild(t);
        this.eqTokens.push(t);
        scaleTo(t, 1, 320, Ease.outBack);
        fadeTo(t, 1, 200);
      }
      x += w + gap;
    });

    // above the numbers, clearing the band beneath the slot for the hand
    this.speedBar.position.set(VW / 2, y - 120);
  }

  private buildCards() {
    const opts = this.problem.options;
    const n = opts.length;
    const gap = 22;
    const totalW = n * this.cardW + (n - 1) * gap;
    const startX = VW / 2 - totalW / 2 + this.cardW / 2;
    const baseY = VH * 0.6; // hand crowds up close under the slot to minimize drag distance
    const center = (n - 1) / 2;

    opts.forEach((val, i) => {
      const card = new Card(val, this.cardW, this.cardH);
      const off = i - center;
      card.homeX = startX + i * (this.cardW + gap);
      card.homeRot = off * 0.05;
      card.homeY = baseY - (1 - (off / (center || 1)) ** 2) * 14; // gentle arc
      card.position.set(card.homeX, VH + 220);
      card.rotation = card.homeRot;
      card.scale.set(0.5);

      card.on("pointerover", () => {
        if (!this.dragging && !card.locked && !this.resolving) {
          card.hoverIn();
          Sfx.hover();
        }
      });
      card.on("pointerout", () => card.hoverOut());
      card.on("pointerdown", (e: FederatedPointerEvent) => this.onCardDown(card, e));

      this.handLayer.addChild(card);
      this.cards.push(card);

      delay(120 + i * 80, () => {
        Sfx.deal();
        moveTo(card, card.homeX, card.homeY, 420, Ease.outBack);
        rotateTo(card, card.homeRot, 420, Ease.outBack);
        scaleTo(card, 1, 420, Ease.outBack);
      });
    });
  }

  // ── input ───────────────────────────────────────────────────────────────
  private onCardDown(card: Card, e: FederatedPointerEvent) {
    if (this.resolving || card.locked || this.dragging) return;
    unlockAudio();

    // double-click / double-tap on a card commits it straight into the slot
    if (this.lastTapCard === card && this.clock - this.lastTapTime <= this.doubleTapMs) {
      this.lastTapCard = null;
      this.commit(card);
      return;
    }
    this.lastTapCard = card;
    this.lastTapTime = this.clock;

    this.dragging = card;
    card.dragging = true;
    this.handLayer.setChildIndex(card, this.handLayer.children.length - 1);
    const lp = this.world.toLocal(e.global);
    this.grabDX = card.x - lp.x;
    this.grabDY = card.y - lp.y;
    this.lastPX = lp.x;
    // also begin a long-press candidate; tick() fires commit if held in place
    this.pressCard = card;
    this.pressStartClock = this.clock;
    this.pressDownX = lp.x;
    this.pressDownY = lp.y;
    card.pop();
    Sfx.pickup();
    this.hideHint();
  }

  private onMove = (e: FederatedPointerEvent) => {
    if (!this.dragging) return;
    const lp = this.world.toLocal(e.global);
    this.dragging.x = lp.x + this.grabDX;
    this.dragging.y = lp.y + this.grabDY;
    const vx = lp.x - this.lastPX;
    this.lastPX = lp.x;
    this.dragging.rotation = clamp(vx * 0.012, -0.22, 0.22);
    this.setSlotHover(this.overSlot(this.dragging));
    if (this.pressCard) {
      const dx = lp.x - this.pressDownX;
      const dy = lp.y - this.pressDownY;
      if (dx * dx + dy * dy > this.holdMoveCancelPx * this.holdMoveCancelPx) this.cancelPress();
    }
  };

  private onUp = () => {
    const card = this.dragging;
    if (!card) return;
    this.dragging = null;
    card.dragging = false;
    this.cancelPress();
    this.setSlotHover(false);
    if (!this.resolving && this.overSlot(card)) {
      this.commit(card);
    } else {
      Sfx.place();
      this.returnCard(card);
    }
  };

  private cancelPress() {
    this.pressCard = null;
    this.pressRing.clear();
  }

  // ── keyboard answers (desktop): type the card's VALUE to commit it ───────
  private onKey = (e: KeyboardEvent) => {
    if (this.state !== "playing" || this.resolving) return;
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    if (e.key >= "0" && e.key <= "9") {
      e.preventDefault();
      unlockAudio();
      this.setTyped((this.typedBuffer + e.key).slice(0, 3));
    } else if (e.key === "Backspace") {
      e.preventDefault();
      this.setTyped(this.typedBuffer.slice(0, -1));
    } else if (e.key === "Escape") {
      this.setTyped("");
    } else if (e.key === "Enter" || e.key === " ") {
      const m = typedMatch(this.typedBuffer, this.liveValues());
      const v = m.kind === "commit" ? m.value : m.kind === "pending" ? m.exact : undefined;
      if (v !== undefined) {
        e.preventDefault();
        this.commitTypedValue(v);
      }
    }
  };

  private liveValues(): number[] {
    return this.cards.filter((c) => !c.destroyed && !c.locked).map((c) => c.value);
  }

  private setTyped(buffer: string) {
    this.typedBuffer = buffer;
    this.typedDeadline = 0;
    const m = typedMatch(buffer, this.liveValues());
    if (m.kind === "commit") {
      this.commitTypedValue(m.value);
      return;
    }
    if (m.kind === "none" && buffer) {
      // dead end — can't grow into any card; quietly reset, no penalty
      this.typedBuffer = "";
      Sfx.place();
    } else if (m.kind === "pending" && m.exact !== undefined) {
      // e.g. typed "1" among [1, 12] — give a longer answer a beat to arrive
      this.typedDeadline = this.clock + 350;
    }
    this.refreshSlotText();
  }

  private commitTypedValue(value: number) {
    const card = this.cards.find((c) => !c.destroyed && !c.locked && c.value === value);
    this.clearTyped();
    if (card) this.commit(card);
  }

  private clearTyped() {
    this.typedBuffer = "";
    this.typedDeadline = 0;
    this.refreshSlotText();
  }

  private refreshSlotText() {
    this.slotQ.text = this.typedBuffer || "?";
    this.slotQ.alpha = this.typedBuffer ? 0.95 : 0.45;
  }

  private drawPressRing(card: Card, p: number) {
    const g = this.pressRing;
    g.clear();
    const r = this.cardW * 0.72;
    const cx = card.x;
    const cy = card.y;
    g.circle(cx, cy, r);
    g.stroke({ width: 6, color: palette.glow, alpha: 0.18 });
    const a0 = -Math.PI / 2;
    const a1 = a0 + p * Math.PI * 2;
    g.moveTo(cx + Math.cos(a0) * r, cy + Math.sin(a0) * r);
    g.arc(cx, cy, r, a0, a1);
    g.stroke({ width: 6, color: palette.good, alpha: 0.95 });
  }

  /** Resolve a card as the player's answer — from a drag-drop or a double-tap. */
  private commit(card: Card) {
    if (this.resolving || card.locked) return;
    // drop any in-flight motion (e.g. the first tap's "return home" tween) so
    // the commit animation owns the card — otherwise a stale tween can yank a
    // double-tapped card back to its starting spot after it reaches the slot.
    cancelMotion(card);
    this.clearTyped(); // a pointer commit abandons any half-typed answer
    this.hideHint();
    if (card.value === this.problem.answer) this.onCorrect(card);
    else this.onWrong(card);
  }

  private overSlot(card: Card): boolean {
    return (
      Math.abs(card.x - this.slot.x) < this.slotW * 0.85 &&
      Math.abs(card.y - this.slot.y) < this.slotH * 0.95
    );
  }

  private setSlotHover(active: boolean) {
    if (active === this.slotHovered) return;
    this.slotHovered = active;
    if (active) Sfx.hover();
  }

  private returnCard(card: Card) {
    moveTo(card, card.homeX, card.homeY, 280, Ease.outBack);
    rotateTo(card, card.homeRot, 240, Ease.outCubic);
    scaleTo(card, 1, 240, Ease.outCubic);
    card.setGlow(0);
  }

  // ── outcomes ────────────────────────────────────────────────────────────
  private onCorrect(card: Card) {
    this.resolving = true;
    card.locked = true;
    card.eventMode = "none";
    this.setSlotHover(false);

    moveTo(card, this.slot.x, this.slot.y, 200, Ease.outBack);
    rotateTo(card, 0, 200, Ease.outBack);
    scaleTo(card, 1, 200, Ease.outBack, () => {
      shake(this.shakeRoot, 7, 240);
      burst(this.fxLayer, this.slot.x, this.slot.y, [palette.good, palette.chip, palette.gold, palette.glow, 0xffffff], 32);
      this.flashSlot(palette.good);
    });

    this.totalCorrect++;
    this.streak++;
    this.bestStreak = Math.max(this.bestStreak, this.streak);

    const { speedBonus, mult, gain } = scoreFor(this.baseChips, this.speedMeter, this.streak);

    Sfx.correct(this.streak);
    this.spawnScorePopup(this.baseChips, mult, gain, speedBonus);
    this.addScore(gain);
    this.updateStreakUI();
    this.levelText.text = `LV ${this.level + 1}`;

    recordAttempt(this.stats, this.problem, true, this.clock - this.roundStartClock);
    recordSession(this.stats, this.score, this.streak);
    saveStats(this.stats);

    for (const c of this.cards) if (c !== card) c.flyOut();

    delay(780, () => this.nextRound());
  }

  private onWrong(card: Card) {
    Sfx.wrong();
    this.flashSlot(palette.bad);
    this.streak = 0;
    this.updateStreakUI();
    recordAttempt(this.stats, this.problem, false, this.clock - this.roundStartClock);
    saveStats(this.stats);
    card.setGlow(0);
    const sr = card.rotation;
    // owned by the card so flyOut's cancelMotion kills it — otherwise this
    // onComplete could re-animate a card that a fast correct answer already
    // swept off (and destroyed), which throws inside the shared ticker
    tween({
      duration: 320,
      ease: Ease.linear,
      owner: card,
      onUpdate: (v) => {
        if (!card.destroyed) card.rotation = sr + Math.sin(v * Math.PI * 4) * (1 - v) * 0.4;
      },
      onComplete: () => {
        if (card.destroyed) return;
        card.rotation = 0;
        this.returnCard(card);
      },
    });
  }

  // ── feedback / HUD ──────────────────────────────────────────────────────
  private flashSlot(color: number) {
    const f = new Graphics();
    f.roundRect(-this.slotW / 2, -this.slotH / 2, this.slotW, this.slotH, 16).fill({ color, alpha: 0.6 });
    f.position.set(this.slot.x, this.slot.y);
    this.boardLayer.addChild(f);
    fadeTo(f, 0, 460, Ease.outCubic, () => f.destroy());
  }

  private addScore(gain: number) {
    const from = this.displayScore;
    this.score += gain;
    const to = this.score;
    tween({
      duration: 520,
      ease: Ease.outCubic,
      onUpdate: (v) => {
        this.displayScore = Math.round(from + (to - from) * v);
        this.scoreValue.text = String(this.displayScore);
      },
    });
    scaleTo(this.scoreValue, 1.28, 130, Ease.outBack, () => scaleTo(this.scoreValue, 1, 220, Ease.outCubic));
  }

  private updateStreakUI() {
    if (this.streak <= 0) {
      this.streakText.text = "";
      return;
    }
    this.streakText.text = `STREAK ×${this.streak}`;
    this.streakText.style.fill = lerpColor(palette.gold, palette.mult, Math.min(this.streak, 10) / 10);
    scaleTo(this.streakText, 1 + Math.min(this.streak, 12) * 0.025, 140, Ease.outBack, () =>
      scaleTo(this.streakText, 1, 200, Ease.outCubic),
    );
  }

  private spawnScorePopup(chips: number, mult: number, gain: number, speedBonus: number) {
    const pop = new Container();

    if (speedBonus >= 4) {
      const fast = new Text({
        text: "LIGHTNING FAST!",
        style: { fontFamily: FONT, fontWeight: "700", fontSize: 22, fill: palette.chip, letterSpacing: 1 },
      });
      fast.anchor.set(0.5);
      fast.position.set(0, -48);
      pop.addChild(fast);
    } else if (speedBonus >= 2) {
      const fast = new Text({
        text: "QUICK!",
        style: { fontFamily: FONT, fontWeight: "700", fontSize: 20, fill: palette.chip },
      });
      fast.anchor.set(0.5);
      fast.position.set(0, -48);
      pop.addChild(fast);
    }

    const eq = new Text({
      text: `${chips} × ${mult}`,
      style: { fontFamily: FONT, fontWeight: "600", fontSize: 28, fill: palette.text },
    });
    eq.anchor.set(0.5);
    eq.position.set(0, -8);
    pop.addChild(eq);

    const plus = new Text({
      text: `+${gain}`,
      style: {
        fontFamily: FONT,
        fontWeight: "700",
        fontSize: 54,
        fill: palette.gold,
        dropShadow: { color: 0x000000, alpha: 0.45, blur: 6, distance: 4, angle: Math.PI / 2 },
      },
    });
    plus.anchor.set(0.5);
    plus.position.set(0, 32);
    pop.addChild(plus);

    // sit over the card and rise — starting at the slot center keeps it clear
    // of the speed bar now sitting above the equation
    pop.position.set(this.slot.x, this.slot.y);
    pop.scale.set(0.4);
    pop.alpha = 0;
    this.fxLayer.addChild(pop);
    scaleTo(pop, 1, 280, Ease.outBack);
    fadeTo(pop, 1, 150);

    const sy = pop.y;
    tween({
      duration: 950,
      delay: 260,
      ease: Ease.outCubic,
      onUpdate: (v) => {
        pop.y = sy - 70 * v;
        pop.alpha = 1 - Math.max(0, (v - 0.55) / 0.45);
      },
      onComplete: () => pop.destroy(),
    });
  }

  private hideHint() {
    if (this.hintHidden) return;
    this.hintHidden = true;
    fadeTo(this.hint, 0, 400, Ease.outCubic);
  }

  // ── per-frame ───────────────────────────────────────────────────────────
  private tick = (tk: Ticker) => {
    this.clock += tk.deltaMS;
    const t = this.clock * 0.001;

    this.spotlight.x = VW / 2 + Math.sin(t * 0.3) * 50;
    this.spotlight.y = VH * 0.4 + Math.cos(t * 0.24) * 36;
    for (const b of this.blobs) {
      b.g.x = b.bx + Math.sin(t * b.sp + b.ph) * 90;
      b.g.y = b.by + Math.cos(t * b.sp * 0.8 + b.ph) * 70;
    }

    if (this.state === "title") {
      this.startButton.tick(this.clock);
      return; // board & HUD are hidden until the player starts
    }

    // a typed exact match that could still grow (1 vs 12) commits on timeout
    if (this.typedDeadline && this.clock >= this.typedDeadline && !this.resolving) {
      const m = typedMatch(this.typedBuffer, this.liveValues());
      this.typedDeadline = 0;
      if (m.kind === "pending" && m.exact !== undefined) this.commitTypedValue(m.exact);
    }

    // long-press to commit: charge a ring under the held card; fire when full
    if (this.pressCard && !this.resolving) {
      const card = this.pressCard;
      const p = pressMeterAt(this.clock - this.pressStartClock, this.holdMs);
      this.drawPressRing(card, p);
      if (p >= 1) {
        this.cancelPress();
        this.dragging = null; // hand off to commit; onUp will early-return
        card.dragging = false;
        navigator.vibrate?.(30); // Android haptic; iOS ignores
        this.commit(card);
      }
    } else if (this.pressCard && this.resolving) {
      this.cancelPress(); // a round resolved mid-press — drop the ring
    }

    const pulse = 0.28 + Math.sin(this.clock * 0.005) * 0.14;
    this.slotGlow.alpha = this.slotHovered ? 0.95 : pulse;
    this.slot.scale.set(this.slotHovered ? 1.08 : 1 + Math.sin(this.clock * 0.005) * 0.02);

    const speedElapsed = this.clock - this.roundStartClock;
    if (!this.resolving) {
      this.speedMeter = speedMeterAt(speedElapsed, this.speedWindow, this.speedHoldMs);
    }
    const frac = this.speedMeter;
    const holding = !this.resolving && speedElapsed < this.speedHoldMs;
    let col: number;
    if (holding) {
      // charged & live: the full bar shimmers and breathes while the max bonus
      // is guaranteed, then it settles and begins to drain
      const pulse = 0.5 + 0.5 * Math.sin(this.clock * 0.02);
      col = lerpColor(palette.good, 0xffffff, 0.15 + pulse * 0.5);
      this.speedFill.scale.y = 1 + pulse * 0.16;
    } else {
      this.speedFill.scale.y = 1;
      col =
        frac > 0.5
          ? lerpColor(palette.gold, palette.good, (frac - 0.5) / 0.5)
          : lerpColor(palette.bad, palette.gold, frac / 0.5);
    }
    this.speedFill.clear();
    const w = this.speedTrackW * frac;
    if (w > 2) this.speedFill.roundRect(-this.speedTrackW / 2, -8, w, 16, 8).fill(col);
  };

  private resize = () => {
    // Size to the VISUAL viewport (the actually-visible area) — on mobile this
    // excludes the URL bar, so the HUD never hides under it; falls back to the
    // window. We own the renderer resize (no `resizeTo: window`) so this is the
    // single source of truth and can't read a stale size mid-transition.
    const vv = window.visualViewport;
    const sw = Math.max(1, Math.round(vv?.width ?? window.innerWidth));
    const sh = Math.max(1, Math.round(vv?.height ?? window.innerHeight));
    this.app.renderer.resize(sw, sh);
    const scale = Math.min(sw / VW, sh / VH);
    this.world.scale.set(scale);
    this.world.position.set((sw - VW * scale) / 2, (sh - VH * scale) / 2);
  };
}
